import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ExternalLink, Maximize2, Minimize2, Bookmark, BookmarkCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScribeGuideService } from '@/services/scribeGuideService';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export interface ScribeGuideProps {
  url: string;
  title: string;
  description?: string;
  mode?: 'slides' | 'movie' | 'scroll';
  category?: string;
  difficulty?: 'beginner' | 'intermediate' | 'advanced';
  className?: string;
  showHeader?: boolean;
  allowFullscreen?: boolean;
  height?: number;
  guideId?: string; // For bookmark functionality
  showBookmark?: boolean;
}

export const ScribeGuide: React.FC<ScribeGuideProps> = ({
  url,
  title,
  description,
  mode = 'slides',
  category,
  difficulty,
  className,
  showHeader = true,
  allowFullscreen = true,
  height = 600,
  guideId,
  showBookmark = true
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  const { user } = useAuth();

  // Check if guide is bookmarked on mount
  useEffect(() => {
    const checkBookmarkStatus = async () => {
      if (guideId && user?.id) {
        try {
          const bookmarked = await ScribeGuideService.isGuideBookmarked(guideId, user.id);
          setIsBookmarked(bookmarked);
        } catch (error) {
          console.error('Error checking bookmark status:', error);
        }
      }
    };

    checkBookmarkStatus();
  }, [guideId, user?.id]);

  // Handle bookmark toggle
  const handleBookmarkToggle = async () => {
    if (!guideId || !user?.id) return;

    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await ScribeGuideService.unbookmarkGuide(guideId, user.id);
        setIsBookmarked(false);
        toast.success('Removed from bookmarks');
      } else {
        await ScribeGuideService.bookmarkGuide(guideId, user.id);
        setIsBookmarked(true);
        toast.success('Added to bookmarks');
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      toast.error('Failed to update bookmark');
    } finally {
      setBookmarkLoading(false);
    }
  };

  // Extract the Scribe ID from the URL
  const getScribeId = (url: string): string | null => {
    // Try multiple patterns to extract the ID
    const patterns = [
      /shared\/.*?__([^/?]+)/,  // Original pattern
      /shared\/[^/]+__([^/?]+)/, // More specific pattern
      /shared\/.*__([^/?]+)$/,   // End of string pattern
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        console.log('Scribe ID extracted:', match[1], 'from URL:', url);
        return match[1];
      }
    }
    
    console.error('Could not extract Scribe ID from URL:', url);
    return null;
  };

  // Generate embed URL
  const getEmbedUrl = (url: string, mode: string): string => {
    // Convert shared URL to embed URL
    // From: https://scribehow.com/shared/How_To_Add_A_Class_In_Hivetivity_Dashboard__HwlvFSyVQ4i-_HpJnQ44lA
    // To: https://scribehow.com/embed/How_To_Add_A_Class_In_Hivetivity_Dashboard__HwlvFSyVQ4i-_HpJnQ44lA
    
    const embedUrl = url.replace('/shared/', '/embed/');
    console.log('Generated embed URL:', embedUrl);
    return embedUrl;
  };

  const embedUrl = getEmbedUrl(url, mode);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setError('Failed to load guide. Please try again or use the direct link.');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'advanced':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!embedUrl || !url.includes('scribehow.com/shared/')) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-red-600">Invalid Guide URL</CardTitle>
          <CardDescription>
            The provided Scribe URL is not valid. Please check the URL and try again.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "relative",
      isFullscreen && "fixed inset-0 z-50 rounded-none",
      className
    )}>
      {showHeader && (
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg">{title}</CardTitle>
              {description && (
                <CardDescription className="mt-1">{description}</CardDescription>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4">
              {category && (
                <Badge variant="secondary" className="text-xs">
                  {category}
                </Badge>
              )}
              {difficulty && (
                <Badge className={cn("text-xs", getDifficultyColor(difficulty))}>
                  {difficulty}
                </Badge>
              )}
              {showBookmark && guideId && user && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBookmarkToggle}
                  disabled={bookmarkLoading}
                  className="h-6 w-6 p-0"
                  title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
                >
                  {bookmarkLoading ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : isBookmarked ? (
                    <BookmarkCheck className="h-3 w-3 text-primary" />
                  ) : (
                    <Bookmark className="h-3 w-3" />
                  )}
                </Button>
              )}
              {allowFullscreen && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullscreen}
                  className="h-6 w-6 p-0"
                >
                  {isFullscreen ? (
                    <Minimize2 className="h-3 w-3" />
                  ) : (
                    <Maximize2 className="h-3 w-3" />
                  )}
                </Button>
              )}
              <Button variant="ghost" size="sm" asChild className="h-6 w-6 p-0">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3" />
                </a>
              </Button>
            </div>
          </div>
        </CardHeader>
      )}

      <CardContent className={cn("p-0", showHeader && "px-6 pb-6")}>
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary" />
                <p className="text-sm text-gray-600">Loading interactive guide...</p>
              </div>
            </div>
          )}

          {error ? (
            <div className="p-8 text-center bg-red-50 rounded-lg">
              <p className="text-red-600 mb-4">{error}</p>
              <Button variant="outline" asChild>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in New Tab
                </a>
              </Button>
            </div>
          ) : (
            <iframe
              src={embedUrl}
              width="100%"
              height={isFullscreen ? '100%' : height}
              frameBorder="0"
              allowFullScreen
              loading="lazy"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              className="rounded-lg"
              title={title}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ScribeGuide;